import React from "react"
import { forwardRef, createStyles, Center } from "@design-system/core"
import { useInteractive } from "@design-system/interactive"
import { __DEV__ } from "@design-system/utils"
import { IconButtonProps } from "./types"

export const IconButton = forwardRef<IconButtonProps, "div">((props, ref) => {
  const { icon, platform, hasBorder, style, ...others } = props
  const {
    isDisabled,
    isHovered,
    isPressed,
    isFocused,
    ...interactiveProps
  } = useInteractive(others)

  const iconElement = React.cloneElement(React.Children.only(icon), {
    style: [platform && platformIconStyles[platform], icon.props?.style],
  })

  return (
    <Center
      tabIndex={0}
      role="button"
      data-focusable="true"
      {...interactiveProps}
      ref={ref}
      style={[
        styles.container,
        hasBorder ? styles.hasBorder : undefined,
        isHovered ? styles.hoverContainer : undefined,
        style,
      ]}
    >
      {iconElement}
    </Center>
  )
})

if (__DEV__) {
  IconButton.displayName = "IconButton"
}

const styles = createStyles((theme) => ({
  container: {
    alignItems: "center",
    cursor: "pointer",
    justifyContent: "center",
    ...theme.iconButton.base.default.container,
  },
  hoverContainer: theme.iconButton.base.states.hover?.container,
  hasBorder: {
    borderStyle: "solid",
    ...theme.iconButton.base.hasBorder.container,
  },
}))

const platformIconStyles = createStyles((theme) => ({
  mobile: theme.iconButton.platforms.mobile?.default.icon,
  desktop: theme.iconButton.platforms.desktop?.default.icon,
}))
